import React from "react";

import { AlertTriangle, FileText, Globe, Info, Lightbulb, Shield, Zap } from "lucide-react";

import ConfidenceMeter from "./ConfidenceMeter";
import MainScoreGauge from "./MainScoreGauge";
import ScoreGauge from "./ScoreGauge";
import { ScoreExplanationTrigger, ScoreMethodologyBlock } from "./score-explanation";

const OverviewView = ({
  ref,
  data,
  technicalAnalysisData,
  completedCount,
  quickInsights,
  topPriorities,
  recommendationsLength,
}) => {
  const totalRecommendations = recommendationsLength;
  const progressPercentage =
    totalRecommendations > 0 ? Math.round((completedCount / totalRecommendations) * 100) : 0;
  const categoryScores = data?.category_scores || {};
  const scoreExplanations = data?.score_explanations || {};
  const technicalScore = technicalAnalysisData?.technical_score ?? data?.technical_score ?? "N/A";
  const technicalBand = technicalAnalysisData?.technical_score_band ?? data?.technical_score_band;
  const aeoScore = categoryScores.AEO ?? "N/A";
  const geoScore = categoryScores.GEO ?? "N/A";
  const aioScore = categoryScores.AIO ?? "N/A";
  // Using ?? (nullish coalescing) instead of || so that a legitimate score of 0
  // is still displayed as 0, while null/undefined shows N/A.
  const sxoScore = categoryScores.SXO ?? "N/A";
  // Transparency redesign: the headline carries an explicit bot-block penalty,
  // a tooling-failure confidence caveat, and a Low/Medium/High crawl-confidence meter.
  // AII is advisory and intentionally NOT shown in this summary UI — it lives
  // only in the detailed view so the alarming advisory number can't read as part of
  // the headline SEO score.
  const scoreBreakdown = data?.score_breakdown || null;
  const crawlProvenance = technicalAnalysisData?.crawl_provenance || null;
  const notVisibleToAi = Boolean(scoreBreakdown?.not_visible_to_ai);
  const confidenceCaveat = crawlProvenance?.confidence_caveat || "";
  const auditConfidence = crawlProvenance?.audit_confidence || "";
  //const validScores = [aeoScore, geoScore, aioScore, sxoScore].filter((score) => score > 0);
  //const sumScores = validScores.reduce((sum, score) => sum + score, 0);
  //const averageScore = validScores.length > 0 ? Math.round(sumScores / validScores.length) : 0;
  const newCategories = [
    // Mapped to AEO (Answer Engine)
    {
      name: "AEO - Answer Engine",
      score: aeoScore,
      band: categoryScores.AEO_band,
      icon: <FileText className="w-6 h-6 text-purple-400" />,
      explanation: scoreExplanations.AEO,
    },
    // Mapped to GEO (Generative Engine)
    {
      name: "GEO - Generative Engine",
      score: geoScore,
      band: categoryScores.GEO_band,
      icon: <Globe className="w-6 h-6 text-orange-400" />,
      explanation: scoreExplanations.GEO,
    },
    // Mapped to AIO (AI Overviews)
    {
      name: "AIO - AI Overviews",
      score: aioScore,
      band: categoryScores.AIO_band,
      icon: <Lightbulb className="w-6 h-6 text-green-400" />,
      explanation: scoreExplanations.AIO,
    },
    // Mapped to AI Crawlability (formerly "Technical SEO")
    {
      name: "AI Crawlability",
      subtitle: "Crawler & Indexing Signals",
      score: technicalScore,
      band: technicalBand,
      icon: <Shield className="w-6 h-6 text-blue-500" />,
      explanation: scoreExplanations.ai_crawlability,
    },
    // Mapped to SXO (Search Experience)
    {
      name: "SXO - Search Experience",
      score: sxoScore,
      band: categoryScores.SXO_band,
      icon: <Zap className="w-6 h-6 text-red-400" />,
      explanation: scoreExplanations.SXO,
    },
    // AI Integration (AII) is advisory and is intentionally excluded from this
    // summary UI — it appears only in the detailed analysis view.
  ];
  const CategoryCard = ({ category }) => {
    return (
      <div className="flex flex-col items-center text-center p-4 rounded-xl bg-surface-container-low border border-surface-container-high hover:border-primary transition-colors">
        <div className="flex-shrink-0 mb-2">
          {React.cloneElement(category.icon, {
            className: category.icon.props.className,
          })}
        </div>
        <div className="mt-2 flex items-center gap-1">
          <p className="font-semibold text-sm text-on-surface-variant leading-snug">
            {category.name}
          </p>
          <ScoreExplanationTrigger explanation={category.explanation} />
        </div>
        {category.advisory && (
          <span className="mb-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-container-high text-on-surface-variant">
            Advisory · not scored
          </span>
        )}
        {category.subtitle && (
          <p className="mb-2 text-xs text-on-surface-variant/70 leading-snug">
            {category.subtitle}
          </p>
        )}
        {!category.subtitle && <div className="mb-2" />}
        <div className="flex-shrink-0">
          <ScoreGauge score={category.score} size="medium" band={category.band} />
        </div>
      </div>
    );
  };
  return (
    <div ref={ref} className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-surface-container-lowest p-6 rounded-3xl shadow-cloud flex flex-col items-center justify-center">
          <MainScoreGauge
            score={data?.quality_score ?? data?.overall_score}
            title="Overall LLM SEO Score"
            band={data?.overall_score_band}
          />
          <ScoreExplanationTrigger explanation={scoreExplanations.overall} />
          <div className="mt-3 flex items-start gap-2 text-xs text-on-surface-variant">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              AI visibility = being accessible to AI crawlers + giving clear, extractable answers +
              earning authority. Google ranking and FAQs help but aren’t the whole picture. The
              score is the content blend, reduced by an explicit penalty when important pages are
              deliberately blocked to AI crawlers.
            </p>
          </div>
          {notVisibleToAi && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-600/10 p-3 text-xs text-red-400">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                <span className="font-semibold">Not visible to AI crawlers.</span> Important pages
                are deliberately blocked to AI search/answer bots, which heavily penalises the
                score. Allow the AI bots in <code>robots.txt</code> and serve real content in raw
                HTML.
              </p>
            </div>
          )}
          {confidenceCaveat && (
            <p className="mt-2 text-xs text-on-surface-variant/80 italic">{confidenceCaveat}</p>
          )}
          {auditConfidence && <ConfidenceMeter level={auditConfidence} className="mt-3" />}
        </div>
        <div className="lg:col-span-2 bg-surface-container-lowest p-6 rounded-3xl shadow-cloud">
          <h3 className="font-bold text-on-surface mb-4">Quick Insights</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-error">{quickInsights.highPriority}</p>
              <p className="text-sm text-on-surface-variant">High Priority Issues</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-yellow-400">{quickInsights.mediumPriority}</p>
              <p className="text-sm text-on-surface-variant">Medium Priority Issues</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-green-400">{completedCount}</p>
              <p className="text-sm text-on-surface-variant">Completed Fixes</p>
            </div>
          </div>
          <h3 className="font-bold text-on-surface mt-8 mb-4">Top 3 Priorities</h3>
          <ul className="space-y-3">
            {topPriorities.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-3 p-3 bg-surface-container-low rounded-lg"
              >
                <div className="w-6 h-6 rounded-full bg-error-container text-error flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">
                  {index + 1}
                </div>
                <div>
                  <p className="font-semibold text-on-surface">{item.text}</p>
                  <p className="text-sm text-on-surface-variant">{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="bg-surface-container-lowest p-6 rounded-3xl shadow-cloud">
        <h3 className="text-xl font-bold text-on-surface mb-6">Category Scores</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {newCategories.map((cat) => (
            <CategoryCard key={cat.name} category={cat} />
          ))}
        </div>
        <ScoreMethodologyBlock methodology={data?.score_methodology} />
      </div>
      <div className="bg-surface-container-lowest p-6 rounded-3xl shadow-cloud">
        <h3 className="text-xl font-bold text-on-surface mb-4">Progress Tracking</h3>
        <div className="text-on-surface-variant font-medium mb-2">
          Implementation Progress:{" "}
          <span className="text-primary font-bold">{progressPercentage}%</span>
        </div>
        <div className="overflow-hidden h-2 mb-2 text-xs flex rounded bg-surface-container-high">
          <div
            style={{ width: `${progressPercentage}%` }}
            className="shadow-none flex flex-col text-center whitespace-pre-wrap text-white justify-center bg-gradient-to-r from-primary to-primary-container transition-all duration-500 rounded-full"
          ></div>
        </div>
        <p className="text-sm text-on-surface-variant mt-3">
          <span className="font-bold text-on-surface">{completedCount}</span> of{" "}
          <span className="font-bold text-on-surface">{totalRecommendations}</span> recommendations
          completed.
        </p>
      </div>
    </div>
  );
};

export default OverviewView;
